import { Show, createEffect, createMemo, on } from "solid-js";
import { useSearchParams } from "@solidjs/router";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { Form, SelectField } from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { useSession } from "../../session/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { ok, type Result } from "../../api/result.ts";
import { ProductCompare } from "./ProductCompare.tsx";
import { comparableProducts } from "./compare.ts";
import { readSelection, writeSelection, type CompareParams, type CompareSelection } from "./compare-url.ts";
import { newestReport, type ScoreOrigin } from "./dashboard.ts";
import type { Api } from "../../api/client.ts";
import type { ReportSnapshot } from "../../api/domain.ts";
import styles from "./admin.module.scss";

/** Where the scores on the wheel come from. */
export type CompareSource = {
  /** `view` is a live run of the chosen view; the rest mirror the dashboard. */
  origin: ScoreOrigin | "view";
  snapshot?: ReportSnapshot;
};

/**
 * The evaluation to compare on. A chosen view is evaluated live, the way the
 * report builder does it, so the comparison follows the view's scope. Without a
 * view the newest report stands in, preferring a locked one, which is the same
 * number the dashboard shows.
 */
export const loadCompareSource = async (api: Api, view: string): Promise<Result<CompareSource>> => {
  if (view !== "") {
    const evaluated = await api.evaluate({ view });
    return evaluated.ok ? ok({ origin: "view", snapshot: evaluated.value }) : evaluated;
  }

  const listed = await api.list("reports");
  if (!listed.ok) return listed;
  const newest = newestReport(listed.value);
  if (newest === undefined) return ok({ origin: "none" });

  const evaluated = await api.reportEvaluation(newest.id);
  if (!evaluated.ok) return evaluated;
  return ok({
    origin: newest.locked_at === null ? "live" : "locked",
    snapshot: evaluated.value,
  });
};

/**
 * Two products face to face, read through a view of the reader's choice.
 *
 * The whole configuration sits in the query string: the view, the pair, the
 * level and the axes. Reloading, bookmarking or sharing the address brings the
 * same picture back.
 */
export const ComparePage = () => {
  const api = useApi();
  const i18n = useI18n();
  const session = useSession();

  const [params, setParams] = useSearchParams<CompareParams>();
  const selection = createMemo(() => readSelection(params));

  /**
   * Chart controls replace the history entry: the back button should leave the
   * screen, not undo a dozen axis toggles. Changing the view is a step the
   * reader may want to go back to.
   */
  const select = (next: CompareSelection) => setParams(writeSelection(next), { replace: true });
  const chooseView = (view: string) =>
    setParams(writeSelection({ ...selection(), view, a: null, b: null, axes: null, open: [] }));

  const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
  const source = createLoader(() => loadCompareSource(api, selection().view));

  /*
   * Only the view decides which evaluation is on screen. Every other control —
   * the pair, the level, the axes, a folded-open row — reads the evaluation
   * that is already loaded.
   *
   * This has to be a memo of the view alone. `selection` rebuilds on any query
   * change, so an effect reading `selection().view` directly is woken by all of
   * them, refetches, and drops the panel back through its loading state. That
   * reads as the page reloading under a reader who only opened a row.
   */
  const view = createMemo(() => selection().view);

  createEffect(on(view, () => void source.reload(), { defer: true }));

  /** The evaluation, once it has a pair to draw; a single product is nothing to compare. */
  const comparable = (found: CompareSource): ReportSnapshot | undefined =>
    found.snapshot !== undefined && comparableProducts(found.snapshot).length >= 2
      ? found.snapshot
      : undefined;

  const sourceNote = (origin: CompareSource["origin"]): string => {
    if (origin === "view") return i18n.t().compare.sourceView;
    if (origin === "locked") return i18n.t().dashboard.scoresFromLocked;
    if (origin === "live") return i18n.t().dashboard.scoresFromLive;
    return i18n.t().dashboard.noEvaluation;
  };

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().compare.title}</h1>
      </div>

      <Panel>
        <Form>
          <SelectField
            label={i18n.t().compare.view}
            hint={i18n.t().compare.viewHint}
            placeholder={i18n.t().compare.newestReport}
            optional
            choices={(loadedValue(views.value()) ?? []).map((view) => ({
              id: view.id,
              label: view.label,
            }))}
            value={selection().view}
            onPick={chooseView}
          />
        </Form>
      </Panel>

      <Async value={source.value()} onRetry={source.reload}>
        {(found) => (
          <Show
            when={comparable(found)}
            fallback={
              <Panel>
                <p class={styles.note}>
                  {found.snapshot === undefined
                    ? sourceNote(found.origin)
                    : i18n.t().compare.tooFew}
                </p>
              </Panel>
            }
          >
            {(snapshot) => (
              <>
                <p class={styles.note}>{sourceNote(found.origin)}</p>
                <ProductCompare
                  snapshot={snapshot()}
                  selection={selection()}
                  onChange={select}
                  customer={session.me()?.customer?.id}
                />
              </>
            )}
          </Show>
        )}
      </Async>
    </>
  );
};
